import { useEffect, useState } from 'react';
import { Page } from '../types';

/**
 * A custom React hook for handling pagination of data fetched from an API.
 *
 * @template T - The type of the items in the paginated response.
 * @param fetchFunction - A function that fetches data based on a query, page number, and page size. It should return a Promise that resolves to a Page<T>.
 * @param initialQuery - The initial search query string.
 * @param pageSize - The number of items to fetch per page.
 * @returns An object containing the current results, loading state, error message, current page, total pages, a function to set the query, and a function to load more results.
 * */
export function usePagination<T>(
  fetchFunction: (
    query: string,
    page: number,
    pageSize: number,
  ) => Promise<Page<T>>, // The fetch function that returns a paginated response
  initialQuery: string,
  pageSize: number,
) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // Effect to reset pagination when query changes
  useEffect(() => {
    // Reset page and results when the query changes
    setCurrentPage(1);
    setResults([]);
  }, [query]);

  // Fetch data whenever query or page changes
  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      try {
        const data = await fetchFunction(query, currentPage, pageSize);

        // If it's the first page, replace the results, else append
        setResults(prevResults =>
          currentPage === 1 ? data.items : [...prevResults, ...data.items],
        );
        setTotalPages(data.totalPages);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
    // IMPORTANT: Fetch function cannot be added as dependency because it might change on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, currentPage, pageSize]);

  // Load more data by incrementing the page
  const loadMore = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return {
    results,
    loading,
    error,
    currentPage,
    totalPages,
    setQuery, // To update the search query
    loadMore, // To load the next page
  };
}
